import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, publicFileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Toaster } from "../components/ui/sonner";
import { PawPrint, Phone, MapPin, ShoppingBag, Check, X } from "lucide-react";

export default function PublicProduct() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/products/${id}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.status === 404 ? "Product not found" : "Failed to load"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold">{err}</h1>
        <Link to="/book" className="mt-4 inline-block text-primary hover:underline">Visit our booking page →</Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  const inr = (n) => `${data.currency} ${Number(n || 0).toFixed(2)}`;
  const hasImage = !!data.image_path;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/book" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><PawPrint className="w-4 h-4" /></div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">{data.store_name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] mt-1 text-muted-foreground">Pet care, available now</div>
            </div>
          </Link>
          <Button asChild variant="outline" size="sm"><Link to="/book" data-testid="book-cta-header">Book a visit</Link></Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 sm:p-10">
        <div className="grid lg:grid-cols-2 gap-8" data-testid="public-product-page">
          {/* Image */}
          <div className="aspect-square rounded-2xl overflow-hidden bg-muted grid place-items-center">
            {hasImage ? (
              <img src={publicFileUrl(data.image_path)} alt={data.name} className="w-full h-full object-cover" />
            ) : (
              <div className="text-muted-foreground flex flex-col items-center gap-2">
                <ShoppingBag className="w-10 h-10" />
                <span className="text-xs uppercase tracking-widest">No image</span>
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {data.category && <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{data.category}</div>}
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mt-1 leading-tight">{data.name}</h1>
            {data.brand && <div className="text-sm text-muted-foreground mt-2">by <span className="font-semibold">{data.brand}</span></div>}

            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-heading text-4xl font-black tracking-tighter">{inr(data.sale_price)}</span>
              {data.mrp && data.mrp > data.sale_price && (
                <span className="text-lg text-muted-foreground line-through">{inr(data.mrp)}</span>
              )}
            </div>

            <div className="mt-3 flex items-center gap-2">
              {data.in_stock ? (
                <Badge variant="secondary" className="gap-1"><Check className="w-3 h-3" /> In stock</Badge>
              ) : (
                <Badge variant="destructive" className="gap-1"><X className="w-3 h-3" /> Out of stock</Badge>
              )}
              {data.barcode && <Badge variant="outline" className="font-mono text-[10px]">GTIN {data.barcode}</Badge>}
            </div>

            {data.description && (
              <p className="text-foreground/80 mt-6 leading-relaxed">{data.description}</p>
            )}

            <Card className="mt-8 p-5 bg-muted/40">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Visit us at</div>
              <div className="font-heading text-xl font-bold mt-1">{data.branch?.name || data.store_name}</div>
              {data.branch?.address && (
                <div className="flex items-start gap-2 mt-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{data.branch.address}</span>
                </div>
              )}
              {data.branch?.phone && (
                <div className="flex items-center gap-2 mt-1 text-sm">
                  <Phone className="w-4 h-4 shrink-0" />
                  <a href={`tel:${data.branch.phone}`} className="font-mono hover:underline">{data.branch.phone}</a>
                </div>
              )}
              <div className="mt-5 grid sm:grid-cols-2 gap-2">
                <Button asChild className="h-11"><Link to="/book" data-testid="book-cta-card"><PawPrint className="w-4 h-4 mr-1" />Book a service</Link></Button>
                {data.branch?.phone && (
                  <Button asChild variant="outline" className="h-11"><a href={`tel:${data.branch.phone}`}><Phone className="w-4 h-4 mr-1" />Call store</a></Button>
                )}
              </div>
            </Card>
          </div>
        </div>
      </main>

      <footer className="border-t border-border mt-12">
        <div className="max-w-5xl mx-auto px-6 py-6 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} {data.store_name} · Pet care, done right.
        </div>
      </footer>
      <Toaster position="top-right" richColors />
    </div>
  );
}
